// frontend/src/components/RoomInfoViewer.tsx
import React, { useState } from "react";
import "./RoomInfoViewer.css";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { Card, Tabs, ScrollArea, Paper, Flex, ActionIcon, Tooltip } from "@mantine/core";
import { FaTable, FaCode, FaRedo } from "react-icons/fa";
import { EnrichSingleRoomButton } from "../DesignTab/EnrichSingleRoomButton";
import { openapi } from "../../openapi";
import { selectIsSystemAdmin } from "../userSelectors";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";
import { selectRoomInfoGroups, selectRoomInfoJson } from "./roomInfoSelectors";

export const RoomInfoViewer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"grid" | "json">("grid");

  const dispatch = useAppDispatch();

  const room = useAppSelector(selectHttpSingleRoomFull);
  const groups = useAppSelector(selectRoomInfoGroups);
  const prettyJson = useAppSelector(selectRoomInfoJson);

  const invalidateSingleRoom = () => {
    if (!room) return;
    dispatch(openapi.util.invalidateTags(["SingleRoom"]));
  };

  const userIsAdmin = useAppSelector(selectIsSystemAdmin);

  return (
    <Card className="room-info-panel" radius={0} w="100%" p="md">
      <div className="room-info-heading">
        <span>ROMINFORMASJON</span>
        <h2>{room?.roomName || "Romdetaljer"}</h2>
        <p>{room?.roomNumber}</p>
      </div>
      <Flex className="room-info-actions" gap="xs" align="center" wrap="wrap">
        {userIsAdmin && <EnrichSingleRoomButton />}
        {userIsAdmin && (
          <Tooltip label="Hent rominformasjonen på nytt" openDelay={400}>
            <ActionIcon
              variant="light"
              size="sm"
              onClick={invalidateSingleRoom}
              disabled={!room}
              aria-label="Oppdater rom"
            >
              <FaRedo size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      </Flex>

      <Tabs
        value={activeTab}
        onChange={(v) => v && setActiveTab(v as "grid" | "json")}
        keepMounted={false}
      >
        <Tabs.List mb="md">
          <Tabs.Tab value="grid" leftSection={<FaTable size={14} />}>
            Oversikt
          </Tabs.Tab>
          <Tabs.Tab value="json" leftSection={<FaCode size={14} />}>
            JSON
          </Tabs.Tab>
        </Tabs.List>

        {/* ========== OVERSIKT ========== */}
        <Tabs.Panel value="grid" pt={0}>
          {!room && <p className="room-info-empty">Ingen rominformasjon tilgjengelig.</p>}
          {groups.map((group) => (
            <section className="room-info-section" key={group.title}>
              <h3>{group.title}</h3>
              <dl className={group.metrics ? "room-info-metrics" : "room-info-properties"}>
                {group.entries.map((row) => (
                  <div key={row.property}>
                    <dt>
                      {row.property}
                      <span title={`Datakilde: ${row.source}`}>{row.source}</span>
                    </dt>
                    <dd title={row.value}>{row.displayValue}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </Tabs.Panel>

        {/* ========== JSON VY ========== */}
        <Tabs.Panel value="json" pt={0} w="100%">
          <ScrollArea mah="65vh">
            <Paper
              component="pre"
              p="md"
              radius="md"
              withBorder
              style={{
                width: "100%",
                boxSizing: "border-box",
                overflowX: "auto",
                margin: 0,
                fontFamily: "monospace",
                fontSize: 12,
                whiteSpace: "pre",
              }}
            >
              {prettyJson}
            </Paper>
          </ScrollArea>
        </Tabs.Panel>
      </Tabs>
    </Card>
  );
};
